import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectHierarchyLabel, Status } from '@weezevent/nacre';
import { stylizeEventOption } from './utils';

export const StatusEvent = React.memo(({ status, removed = null }) => {
    const { t } = useTranslation();

    const color = React.useMemo(() => {
        if (removed) {
            return 'red';
        }
        switch (status) {
            case 'closed':
                return 'grey';
            case 'draft':
                return 'orange';
            default:
                return 'green';
        }
    }, [status, removed]);

    return <Status style={{ marginLeft: '20px' }} text={t(`ticket.rate_template.${removed ? 'deleted' : status}`)} color={color} />;
});

export default function SelectEvents({ onChange, multi = false, events, onFormatOption = null, ...rest }) {
    const { t } = useTranslation();
    const [querySearch, setQuerySearch] = React.useState('');

    const handleQuery = React.useCallback(value => {
        setQuerySearch(value);
    });

    const eventOptions = React.useMemo(() => {
        if (!events) return [];

        // Use the custom formatting function if available
        if (onFormatOption) {
            return events.map(e => onFormatOption(e));
        }

        // Fallback to the default formatting
        return events.map(e => ({
            key: e.id,
            text: stylizeEventOption(e),
            name: e.name,
            value: e.id
        }));
    }, [events, onFormatOption]);

    const eventsList = React.useMemo(() => {
        if (!querySearch) {
            return eventOptions;
        }
        const reg = new RegExp(querySearch, 'gi');
        return eventOptions?.filter(ev => ev.name.match(reg));
    }, [eventOptions, querySearch]);

    const onLabelSelected = selected => {
        return selected.length ? t('ticket.export.modal.selected', { count: selected.length }) : '';
    };
    const handleLabel = React.useCallback(
        value => {
            return eventOptions.find(o => value?.key === o.key)?.name;
        },
        [eventOptions]
    );
    return (
        <SelectHierarchyLabel
            multi={multi}
            widthIndent="10px"
            onLabelSelected={multi ? onLabelSelected : handleLabel}
            externalOptions={eventsList}
            options={eventOptions}
            label={t('ticket.waiting_list.create.purchase_rate.event_label')}
            placeholder={t('ticket.waiting_list.create.purchase_rate.event_placeholder')}
            onChange={onChange}
            onExternalSearch={handleQuery}
            searchValue={querySearch}
            {...rest}
        />
    );
}
